"use client";

import { useEffect, useState, useRef, FormEvent } from "react";
import { useRouter } from 'next/navigation';
import { fetchComments, Comment } from "@/lib/comment";
import { addCommentAction, deleteCommentAction } from "@/lib/commentActions";
import { Button } from "@/components/ui/button"; // Shadcn Button
import { Textarea } from "@/components/ui/textarea"; // Shadcn Textarea
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"; // Shadcn Avatar
import { User } from "@supabase/supabase-js"; // Supabase User type
import { createClient } from "@/utils/supabase/client"; // Supabase client
import { toast } from "sonner"; // Sonner for notifications
import Swal from 'sweetalert2'; // sweetalert2 import 추가

interface CommentSectionProps {
  postId: string; // This is the game ID or slug used as post_id
  userId: string; // Logged-in user ID or 'Anon12'
}

export default function CommentSection({ postId, userId }: CommentSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false); // For loading comments
  const [isSubmitting, setIsSubmitting] = useState(false); // For submitting new comment
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const supabase = createClient();
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // --- Fetch current user --- 
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    getUser();
  }, [supabase]);

  // --- Load comments --- 
  async function loadComments() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchComments(postId);
      setComments(data);
    } catch (err) {
      setError("댓글 로딩 실패");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]); // postId 변경 시에만 댓글 로드

  // --- Handle Add Comment --- 
  async function handleAddCommentSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!newComment.trim() || !currentUser) { // Check currentUser
      if (!currentUser) toast.error("로그인이 필요합니다.");
      return;
    } 
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    formData.append('gameSlug', postId); // Use postId (game ID/slug) for revalidation path

    // --- 낙관적 업데이트 준비 --- 
    const tempCommentId = Date.now(); // 임시 ID
    const optimisticComment: Comment = {
      id: tempCommentId,
      post_id: postId,
      user_id: currentUser.id,
      content: newComment,
      created_at: new Date().toISOString(),
      // 프로필 정보는 서버에서 가져와야 하므로 우선 null 처리
      // 또는 현재 로그인한 사용자의 프로필 정보를 미리 가져와 사용 가능
      profiles: { 
        nickname: currentUser.user_metadata?.nickname || currentUser.email || '나',
        avatarUrl: currentUser.user_metadata?.avatarUrl
      },
    };
    // --- --- 

    // UI에 먼저 반영
    setComments(prev => [optimisticComment, ...prev]);
    setNewComment(""); 
    formRef.current?.reset();

    try {
      const result = await addCommentAction(formData);
      if (result?.success && result.comment) {
        toast.success("댓글이 작성되었습니다.");
        // 서버 응답으로 실제 댓글 정보 업데이트 (ID 등)
        setComments(prev => 
            prev.map(c => c.id === tempCommentId ? { ...result.comment, profiles: c.profiles } : c)
        );
        router.refresh(); // 백그라운드에서 데이터 동기화
      } else {
        setError(result?.error || "댓글 작성 실패");
        toast.error(result?.error || "댓글 작성 실패");
        // 실패 시 낙관적 업데이트 롤백
        setComments(prev => prev.filter(c => c.id !== tempCommentId));
      }
    } catch (err) {
      const errorMsg = "댓글 작성 중 오류";
      setError(errorMsg);
      toast.error(errorMsg);
      console.error(err);
      // 실패 시 낙관적 업데이트 롤백
      setComments(prev => prev.filter(c => c.id !== tempCommentId));
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- Handle Delete Comment --- 
  async function handleDeleteCommentSubmit(event: FormEvent<HTMLFormElement>) {
      event.preventDefault();
      console.log("handleDeleteCommentSubmit 시작");
      setError(null);
      
      const formData = new FormData(event.currentTarget);
      formData.append('gameSlug', postId);
      const commentIdStr = formData.get('commentId') as string;
      const commentId = Number(commentIdStr);
      console.log("삭제 대상 commentId:", commentId);

      if (isNaN(commentId)) {
        console.error("유효하지 않은 commentId:", commentIdStr);
        return;
      }

      // window.confirm 대신 sweetalert2 사용
      console.log("삭제 확인 대화상자 표시 (sweetalert2)");
      const result = await Swal.fire({
        title: "정말 삭제하시겠습니까?",
        text: "삭제된 댓글은 복구할 수 없습니다.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "삭제",
        cancelButtonText: "취소"
      });

      if (!result.isConfirmed) {
        console.log("삭제 작업 취소");
        return;
      }

      console.log("삭제 작업 진행...");
      // --- 낙관적 업데이트 --- 
      const originalComments = [...comments];
      setComments(prev => prev.filter((comment) => comment.id !== commentId));
      // --- --- 

      setIsLoading(true); 

      try {
          console.log("deleteCommentAction 호출 시도...");
          const deleteResult = await deleteCommentAction(formData); // 변수 이름 변경 (result 중복)
          console.log("deleteCommentAction 결과:", deleteResult);

          if (deleteResult?.success) {
              toast.success("댓글이 삭제되었습니다."); // 성공 알림은 유지
              router.refresh();
          } else {
              setError(deleteResult?.error || "댓글 삭제 실패");
              toast.error(deleteResult?.error || "댓글 삭제 실패"); // 실패 알림은 유지
              setComments(originalComments);
          }
      } catch (err) {
          const errorMsg = "댓글 삭제 중 오류";
          setError(errorMsg);
          toast.error(errorMsg);
          console.error("handleDeleteCommentSubmit 오류 캐치:", err);
          setComments(originalComments);
      } finally {
          setIsLoading(false);
          console.log("handleDeleteCommentSubmit 종료");
      }
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">댓글 ({comments.length})</h3>
      {error && <p className="text-red-500 text-sm">오류: {error}</p>}
      
      {/* === 댓글 입력 폼 === */} 
      {currentUser ? ( // 로그인 했을 때만 표시
        <form ref={formRef} onSubmit={handleAddCommentSubmit} className="flex items-start space-x-3">
          <Avatar className="mt-1"> 
            <AvatarImage src={currentUser.user_metadata?.avatar_url} alt={currentUser.user_metadata?.nickname || '사용자'} />
            <AvatarFallback>
              {(currentUser.user_metadata?.nickname?.[0] || currentUser.email?.[0] || 'U').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <input type="hidden" name="postId" value={postId} /> 
            <Textarea
              name="content" // Add name attribute
              placeholder="댓글을 추가하세요..."
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              required
              disabled={isSubmitting}
              className="mb-2"
            />
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !newComment.trim()}
            >
              {isSubmitting ? '작성 중...' : '댓글 작성'}
            </Button>
          </div>
        </form>
      ) : ( // 로그인 안 했을 때 안내 메시지
        <div className="text-center text-sm text-gray-500 border p-4 rounded-md">
          댓글을 작성하려면 <a href="/account/login" className="text-blue-500 hover:underline">로그인</a>해주세요.
        </div>
      )}

      {/* === 댓글 목록 === */} 
      {isLoading && comments.length === 0 && <div className="text-center py-4 text-gray-500">댓글 로딩 중...</div>} 
      {!isLoading && comments.length === 0 && (
          <div className="text-center py-4 text-gray-500">아직 댓글이 없습니다. 첫 댓글을 작성해보세요!</div>
      )}
      <ul className="space-y-4">
        {comments.map((comment) => (
          <li key={comment.id} className="flex items-start space-x-3">
            <Avatar>
              <AvatarImage 
                src={comment.profiles?.avatarUrl} 
                alt={comment.profiles?.nickname || '익명'} 
              />
              <AvatarFallback>
                {(comment.profiles?.nickname?.[0] || '익').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{comment.profiles?.nickname ?? '익명'}</span> 
                <span className="text-xs text-gray-500">
                  {new Date(comment.created_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
              <p className="text-sm mt-1">{comment.content}</p>
              {/* 삭제 버튼 (작성자 본인 + 로그인 상태일 때만) */} 
              {currentUser && comment.user_id === currentUser.id && (
                <form onSubmit={handleDeleteCommentSubmit} className="mt-1">
                  <input type="hidden" name="commentId" value={comment.id} />
                  <Button
                    type="submit"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-500 hover:bg-red-100 h-auto p-1 disabled:opacity-50"
                    disabled={isLoading} // Use general loading state
                  >
                    삭제
                  </Button>
                </form>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
